import { useEffect, useRef, useState } from 'react'
import { formatBytes } from '@/lib/attachments'

/**
 * PhotoAttachInput — reusable photo picker for logger forms.
 *
 * The parent holds the File array in state and passes it back down; this component
 * ONLY handles preview + accept/reject validation + camera picker. The parent is
 * responsible for calling uploadAttachment() after the event is emitted.
 *
 * Multi-photo (up to `max`). Displays a horizontal scroller of thumbnails when photos
 * are selected + a "+ Add" tile that opens the picker again.
 */
interface Props {
  photos: File[]
  onChange: (photos: File[]) => void
  max?: number
  /** Label shown when empty (e.g. "Attach photo (optional)") */
  label?: string
  /** Hint under label */
  hint?: string
  /** Compact mode — smaller drop-zone, used in tight logger forms */
  compact?: boolean
}

export function PhotoAttachInput({
  photos,
  onChange,
  max = 4,
  label = 'Photos',
  hint = 'JPG · PNG · HEIC · WebP · max 20 MB each',
  compact = false,
}: Props) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [previews, setPreviews] = useState<string[]>([])

  useEffect(() => {
    const urls = photos.map((f) => URL.createObjectURL(f))
    setPreviews(urls)
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [photos])

  function handleFiles(list: FileList | null) {
    setError(null)
    if (!list || list.length === 0) return
    const incoming = Array.from(list)
    const accepted: File[] = []
    for (const f of incoming) {
      const isImage = /^image\//.test(f.type)
      const isVideo = /^video\//.test(f.type)
      if (!isImage && !isVideo) {
        setError(`"${f.name}" isn't an image or video. Pick a JPG, PNG, HEIC, WebP, or MP4.`)
        continue
      }
      const maxSize = isVideo ? 100 * 1024 * 1024 : 20 * 1024 * 1024
      if (f.size > maxSize) {
        setError(`"${f.name}" is too large (${formatBytes(f.size)}). Max ${isVideo ? '100' : '20'} MB.`)
        continue
      }
      accepted.push(f)
    }
    if (accepted.length === 0) return
    const combined = [...photos, ...accepted].slice(0, max)
    onChange(combined)
    if (fileInput.current) fileInput.current.value = ''
  }

  function removeAt(i: number) {
    const next = photos.slice()
    next.splice(i, 1)
    onChange(next)
  }

  const dropClass = compact
    ? 'py-4'
    : 'py-6'

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-caption font-medium text-text-secondary">{label}</div>
        {photos.length > 0 && (
          <div className="text-tag text-text-tertiary">{photos.length} / {max}</div>
        )}
      </div>

      {photos.length === 0 && (
        <label className={`block cursor-pointer glass-tile ${dropClass} text-center`} style={{ borderStyle: 'dashed', borderColor: 'rgb(var(--divider-subtle) / 0.25)' }}>
          <input
            ref={fileInput}
            type="file"
            accept="image/*,video/*"
            multiple
            capture="environment"
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
          />
          <div className="text-body font-medium text-text-primary">＋ Tap to take a photo or video</div>
          <div className="mt-1 text-tag text-text-tertiary">{hint}</div>
        </label>
      )}

      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
          {previews.map((url, i) => (
            <div key={i} className="relative shrink-0">
              {/^video\//.test(photos[i]?.type ?? '') ? (
                <video src={url} className="h-24 w-24 rounded-r-sm object-cover" muted playsInline style={{ boxShadow: '0 4px 12px -4px rgba(15,23,42,0.20)' }} />
              ) : (
                <img
                  src={url}
                  alt={photos[i]?.name ?? 'attachment'}
                  className="h-24 w-24 rounded-r-sm object-cover"
                  style={{ boxShadow: '0 4px 12px -4px rgba(15,23,42,0.20)' }}
                />
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute -top-1 -right-1 h-6 w-6 rounded-full flex items-center justify-center text-white"
                style={{
                  background: 'rgba(15,23,42,0.72)',
                  backdropFilter: 'blur(6px)',
                }}
                aria-label={`Remove ${photos[i]?.name ?? 'photo'}`}
              >
                ×
              </button>
              <div className="mt-1 text-tag text-text-tertiary truncate w-24 text-center">
                {photos[i] ? formatBytes(photos[i].size) : ''}
              </div>
            </div>
          ))}
          {photos.length < max && (
            <label className="shrink-0 h-24 w-24 rounded-r-sm border border-dashed border-divider-subtle/40 flex flex-col items-center justify-center cursor-pointer text-text-tertiary bg-bg-elevated/60">
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                capture="environment"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
              />
              <span className="text-title font-display">＋</span>
              <span className="text-tag mt-0.5">Add</span>
            </label>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 text-caption" style={{ color: '#E85E6E' }}>
          {error}
        </div>
      )}
    </div>
  )
}
